import { ISaSmsApprovalRow } from '@interfaces/sa-sms';
import { Button, notification, Space, Table, Tag } from 'antd';
import { useSaSmsWrite } from 'hooks/use-sa-sms-api';
import { useSaSmsUserNames } from 'hooks/use-sa-sms-user-names';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SaSmsReasonModal } from './sa-sms-reason-modal';

const COLOR: Record<string, string> = {
    pending: 'processing',
    approved: 'success',
    rejected: 'error',
    expired: 'default',
};

type Props = {
    rows: ISaSmsApprovalRow[];
    loading: boolean;
    canEdit: boolean;
    onOpenTenant: (id: string) => void;
    onDone: () => void;
};

/** Over-limit approval requests with approve / reject (a note is mandatory and is shown to the audit trail). */
export const SmsApprovalsTable: React.FC<Props> = ({
    rows,
    loading,
    canEdit,
    onOpenTenant,
    onDone,
}) => {
    const { t, i18n } = useTranslation(['sa-sms']);
    const { send } = useSaSmsWrite();
    const [target, setTarget] = useState<{
        row: ISaSmsApprovalRow;
        decision: 'approve' | 'reject';
    } | null>(null);
    const names = useSaSmsUserNames(rows.flatMap((r) => [r.requestedBy, r.decidedBy]));
    const when = (v: string | null) => (v ? new Date(v).toLocaleString(i18n.language) : '-');

    const confirm = async ({ reason }: { reason: string }) => {
        if (!target) return;
        await send('post', `/approvals/${target.row.id}/decision`, {
            decision: target.decision,
            note: reason,
        });
        notification.success({ message: t('saved') });
        setTarget(null);
        onDone();
    };

    return (
        <>
            <Table<ISaSmsApprovalRow>
                rowKey="id"
                loading={loading}
                dataSource={rows}
                pagination={false}
                scroll={{ x: 1000 }}
            >
                <Table.Column<ISaSmsApprovalRow>
                    title={t('credits.date')}
                    render={(_, r) => when(r.createdAt)}
                />
                <Table.Column<ISaSmsApprovalRow>
                    title={t('list.tenant')}
                    render={(_, r) => (
                        <a onClick={() => onOpenTenant(r.tenantId)}>{r.tenantName ?? r.tenantId}</a>
                    )}
                />
                <Table.Column<ISaSmsApprovalRow>
                    title={t('approvals.recipients')}
                    align="right"
                    render={(_, r) => r.recipients.toLocaleString(i18n.language)}
                />
                <Table.Column<ISaSmsApprovalRow>
                    title={t('approvals.segments')}
                    align="right"
                    render={(_, r) => r.segments.toLocaleString(i18n.language)}
                />
                <Table.Column<ISaSmsApprovalRow>
                    title={t('approvals.requestedBy')}
                    render={(_, r) => (r.requestedBy ? names[r.requestedBy] ?? r.requestedBy : '-')}
                />
                <Table.Column<ISaSmsApprovalRow>
                    title={t('approvals.status')}
                    render={(_, r) => (
                        <Tag color={COLOR[r.status]}>{t(`approvals.${r.status}`)}</Tag>
                    )}
                />
                <Table.Column<ISaSmsApprovalRow>
                    title={t('approvals.decidedBy')}
                    render={(_, r) => (r.decidedBy ? names[r.decidedBy] ?? r.decidedBy : '-')}
                />
                <Table.Column<ISaSmsApprovalRow>
                    title={t('approvals.note')}
                    render={(_, r) => r.note ?? '-'}
                />
                <Table.Column<ISaSmsApprovalRow>
                    title=""
                    render={(_, r) =>
                        canEdit &&
                        r.status === 'pending' && (
                            <Space>
                                <Button
                                    type="primary"
                                    size="small"
                                    onClick={() => setTarget({ row: r, decision: 'approve' })}
                                >
                                    {t('approvals.approve')}
                                </Button>
                                <Button
                                    danger
                                    size="small"
                                    onClick={() => setTarget({ row: r, decision: 'reject' })}
                                >
                                    {t('approvals.reject')}
                                </Button>
                            </Space>
                        )
                    }
                />
            </Table>
            <SaSmsReasonModal
                open={!!target}
                danger={target?.decision === 'reject'}
                title={target ? t(`approvals.confirm.${target.decision}`) : ''}
                onCancel={() => setTarget(null)}
                onConfirm={confirm}
            />
        </>
    );
};
